import { Container, Graphics, filters } from "pixi.js"
import { CompositeTilemap } from "@pixi/tilemap"
import { NormalBlendTilemap } from "./normal_blend_tilemap"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { Entity } from "game-common/models"
import { VisibilityFade } from "./visibility_fade"
import { isExteriorFloorFall } from "game-common/floor_fall"

/**
 * Configuration options for the SkyManager
 */
export interface SkyManagerConfig {
    skySurface: Container
    tilemapMetaProvider: TileMapMetaProvider
}

/**
 * Interface for sky management functionality
 */
export interface ISkyManager {
    // Sky visibility control
    setStaticHidingAllowed(allowed: boolean): void
    updatePlayerLocation(entity: any, force?: boolean): void

    // Sky rendering
    clearSkySurfaces(): void
    getSkyTilemapRenderer(): CompositeTilemap
    getSkySpriteSurfaceAbove(): Container
    getSkySpriteSurfaceBelow(): Container

    // Sky state
    isUnderSky(): boolean | undefined
    isSkyHidingAllowed(): boolean
    getSkyAlpha(): number

    // Configuration
    moveTo(skySurface: Container): void
    updateTilemapMetaProvider(tilemapMetaProvider: TileMapMetaProvider): void
    resetSkyState(): void
}

/**
 * Manages sky-related rendering and visibility
 * Handles sky tile rendering, visibility transitions, and fading effects
 */
export class SkyManager implements ISkyManager {
    private skySurface: Container
    private skyTilemapRenderer: CompositeTilemap
    private skySpriteSurfaceAbove: Container
    private skySpriteSurfaceBelow: Container
    // `filters` is a const object in pixi's typings, not a namespace, so it cannot be
    // used in a type position directly.
    private skyAlphFilter: InstanceType<typeof filters.AlphaFilter>
    private skyHidingAllowed: boolean = true
    private currentUnderSky: boolean | undefined = undefined
    private visibilityFade = new VisibilityFade()
    private tilemapMetaProvider: TileMapMetaProvider | undefined

    constructor(config: SkyManagerConfig) {
        this.skySurface = config.skySurface
        this.tilemapMetaProvider = config.tilemapMetaProvider

        // Initialize sky components
        this.skyTilemapRenderer = new NormalBlendTilemap()
        this.skySpriteSurfaceAbove = new Graphics()
        this.skySpriteSurfaceBelow = new Graphics()
        this.skyAlphFilter = new filters.AlphaFilter(1.0)

        // Setup sky surface. The AlphaFilter is attached ONLY while a fade is in progress (see
        // updatePlayerLocation) -- a permanently attached filter forces the whole sky surface to
        // render-to-texture every frame even at full alpha, a constant cost on integrated GPUs.
        this.skySurface.addChild(this.skySpriteSurfaceBelow)
        this.skySurface.addChild(this.skyTilemapRenderer)
        this.skySurface.addChild(this.skySpriteSurfaceAbove)
    }

    // ============================================================================
    // Sky Visibility Control
    // ============================================================================

    /**
     * Set whether sky hiding is allowed
     */
    public setStaticHidingAllowed = (allowed: boolean): void => {
        this.skyHidingAllowed = allowed
    }

    /**
     * Update sky visibility based on player location
     */
    public updatePlayerLocation = (entity: Entity, force?: boolean): void => {
        if (!this.tilemapMetaProvider || !entity) {
            return
        }

        // Sample coverage every frame: feet and live roof edits can cross a boundary
        // without changing the player's body cell. Reversals retain the current opacity.
        this.currentUnderSky = !isExteriorFloorFall(entity.floorFall) && this.tilemapMetaProvider.isUnderRoofAt(entity.location)
        this.visibilityFade.update(!this.currentUnderSky || !this.skyHidingAllowed)
        this.skySurface.visible = this.visibilityFade.visible
        this.skyAlphFilter.alpha = this.visibilityFade.alpha
        // Keep the grouped alpha pass only while translucent; opaque/hidden ceilings
        // need no render-to-texture filter.
        this.skySurface.filters = this.visibilityFade.visible && this.visibilityFade.alpha < 1
            ? [this.skyAlphFilter] : null
    }

    // ============================================================================
    // Sky Rendering
    // ============================================================================

    /**
     * Clear all sky surfaces
     */
    public clearSkySurfaces = (): void => {
        this.skyTilemapRenderer.clear()
        this.skySpriteSurfaceAbove.removeChildren()
        this.skySpriteSurfaceBelow.removeChildren()
    }

    /**
     * Get the sky tilemap renderer
     */
    public getSkyTilemapRenderer = (): CompositeTilemap => {
        return this.skyTilemapRenderer
    }

    /**
     * Get the sky sprite surface above
     */
    public getSkySpriteSurfaceAbove = (): Container => {
        return this.skySpriteSurfaceAbove
    }

    /**
     * Get the sky sprite surface below
     */
    public getSkySpriteSurfaceBelow = (): Container => {
        return this.skySpriteSurfaceBelow
    }

    // ============================================================================
    // Sky State
    // ============================================================================

    /**
     * Check if currently under sky
     */
    public isUnderSky = (): boolean | undefined => {
        return this.currentUnderSky
    }

    /**
     * Check if sky hiding is allowed
     */
    public isSkyHidingAllowed = (): boolean => {
        return this.skyHidingAllowed
    }

    public getSkyAlpha = (): number => this.skyAlphFilter.alpha

    // ============================================================================
    // Configuration
    // ============================================================================

    /**
     * Update the tilemap meta provider
     */
    public updateTilemapMetaProvider = (tilemapMetaProvider: TileMapMetaProvider): void => {
        this.tilemapMetaProvider = tilemapMetaProvider
    }

    /**
     * Reset sky state (called when changing rooms)
     */
    /**
     * Hand this map's sky layers to another container. The one left behind belongs to someone else
     * now, so it goes back to plain and visible; this manager's own fade restarts.
     */
    public moveTo = (skySurface: Container): void => {
        if (skySurface === this.skySurface) return
        const previous = this.skySurface
        skySurface.addChild(this.skySpriteSurfaceBelow, this.skyTilemapRenderer, this.skySpriteSurfaceAbove)
        previous.visible = true
        previous.filters = null
        this.skySurface = skySurface
        this.resetSkyState()
    }

    public resetSkyState = (): void => {
        this.skyHidingAllowed = true
        this.currentUnderSky = undefined
        this.visibilityFade.reset()
        this.skyAlphFilter.alpha = 1
        this.skySurface.visible = true
        this.skySurface.filters = null
    }
}
